"use client";

/**
 * Bold Eco Theme v1 - Vercel Commerce Style
 * ==========================================
 * A faithful adaptation of Vercel Commerce design
 * Fully integrated with Ecommaps data layer
 */

import { useEffect } from 'react';
import { ThemeProvider } from '@/themes/core/data-provider';
import { Navbar } from './components/navbar';
import { Footer } from './components/footer';
import TrackOrderClient from '@/components/storefront/TrackOrderClient'; // Direct Import
import dynamic from 'next/dynamic';
import type { ThemeProps } from './types';

// Dynamic Views (Code Splitting to fix 2.1s TBT)
const ProductView = dynamic(() => import('./views/ProductView').then(mod => mod.ProductView));
const CollectionView = dynamic(() => import('./views/CollectionView').then(mod => mod.CollectionView));
const CheckoutView = dynamic(() => import('./views/CheckoutView').then(mod => mod.CheckoutView));
const HomeView = dynamic(() => import('./views/HomeView').then(mod => mod.HomeView));
const PageView = dynamic(() => import('./views/PageView').then(mod => mod.PageView));
const LoginView = dynamic(() => import('./views/LoginView').then(mod => mod.LoginView));
const RegisterView = dynamic(() => import('./views/RegisterView').then(mod => mod.RegisterView));
const AccountView = dynamic(() => import('./views/AccountView').then(mod => mod.AccountView));
import type { ThemeProps } from './types';

// ═══════════════════════════════════════════════════════════════
// Main Theme Component
// ═══════════════════════════════════════════════════════════════

export default function BoldEcoTheme({
    site,
    settings,
    products,
    collections,
    route,
    basePath = '',
    customMenus = [],
    initialPage
}: ThemeProps) {
    // Parse route
    const view = route?.view || 'HOME';
    const slug = route?.slug || null;
    const query = route?.query || null;

    // Build theme data for context
    const themeData = {
        site: site as any,
        settings: (settings || {}) as Record<string, unknown>,
        route: { view: view as string, slug, query },
        basePath,
        initialProducts: products as any[],
        initialCollections: collections as any[],
    };

    // ─────────────────────────────────────────────────────────────
    // NEW: Force Light Mode exclusively for the Bidaya Theme
    // ─────────────────────────────────────────────────────────────
    useEffect(() => {
        const enforceLightMode = () => {
            const html = document.documentElement;
            if (html.classList.contains('dark')) {
                html.classList.remove('dark');
            }
            if (!html.classList.contains('light')) {
                html.classList.add('light');
            }
            // Strip style attribute that might force color-scheme string
            html.style.colorScheme = 'light';
        };

        // Run immediately
        enforceLightMode();

        // Observe class changes locally in case next-themes forces dark back
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class' || mutation.attributeName === 'style') {
                    if (document.documentElement.classList.contains('dark')) {
                        enforceLightMode();
                    }
                }
            });
        });

        observer.observe(document.documentElement, { attributes: true });

        return () => {
            observer.disconnect();
        };
    }, []);

    // Render content based on route
    const renderContent = () => {
        switch (view) {
            case 'PRODUCT':
                return <ProductView slug={slug || ''} siteId={site?.id || ''} settings={settings as any} />;

            case 'COLLECTION':
            case 'SEARCH':
                return <CollectionView slug={slug || undefined} query={query || undefined} siteId={site?.id || ''} />;

            case 'CHECKOUT':
                return <CheckoutView />;

            case 'PAGE':
                return <PageView slug={slug || ''} siteId={site?.id || ''} initialPage={initialPage} />;

            case 'LOGIN':
                return <LoginView />;

            case 'REGISTER':
                return <RegisterView />;

            case 'ACCOUNT':
                return <AccountView />;

            case 'TRACKING':
                return (
                    <div className="py-12 flex justify-center">
                        <TrackOrderClient locale={(settings as any)?.locale || 'ar'} siteId={site?.id || ''} />
                    </div>
                );

            case 'HOME':
            default:
                return (
                    <HomeView
                        products={products}
                        collections={collections}
                        settings={settings as any}
                        site={site as any}
                    />
                );
        }
    };

    // CSS Variables for Colors & Fonts
    const colors = (settings?.colors || {}) as any;
    const typography = (settings?.typography || {}) as any;

    const primaryColor = colors.primary || '#000000';
    const secondaryColor = colors.secondary || '#ffffff';
    const accentColor = colors.accent || '#fbbf24';
    const backgroundColor = colors.background || '#ffffff';
    const textColor = colors.text || '#171717';
    const fontFamily = typography.fontFamily || 'Inter';

    return (
        <ThemeProvider data={themeData}>
            <style jsx global>{`
                :root {
                    --background: ${backgroundColor};
                    --foreground: ${textColor};
                    --primary: ${primaryColor};
                    --primary-foreground: #ffffff;
                    --secondary: ${secondaryColor};
                    --secondary-foreground: #000000;
                    --accent: ${accentColor};
                    --accent-foreground: #000000;
                    --font-sans: "${fontFamily}", sans-serif;
                }
                body {
                    background-color: var(--background);
                    color: var(--foreground);
                    font-family: var(--font-sans);
                }
            `}</style>

            {/* Load Google Fonts if needed */}
            <link
                href={`https://fonts.googleapis.com/css2?family=${fontFamily.replace(' ', '+')}:wght@400;500;700&display=swap`}
                rel="stylesheet"
            />

            <div className={`min-h-screen bg-background text-foreground ${fontFamily}`}>
                {/* Navbar */}
                <Navbar
                    siteName={site?.name || 'المتجر'}
                    logo={site?.logo_url}
                    basePath={basePath}
                    customMenus={customMenus}
                />

                {/* Main Content */}
                <main>
                    {renderContent()}
                </main>

                {/* Footer */}
                {/* Footer */}
                <Footer
                    site={site as any}
                    customMenus={customMenus}
                />
            </div>
        </ThemeProvider>
    );
}
